"use client";

// ============================================================
// CLUBHUB – Nachrichten: Zone 1, wer bekommt die Nachricht
// ============================================================
// Kanal, Zweck, Gruppen, Status, Personen, Ausschluesse — und
// daneben der Zaehler, der live sagt, was daraus wird. Wer ohne
// Einwilligung oder ohne Adresse ist und wer einen Brief bekommt,
// steht dort ausdruecklich, mit Namen. Gerechnet wird in
// `lib/nachrichten/empfaenger.ts` — dieselbe Regel wie beim Versand.
//
// DIE ZONE NIMMT DIE VOLLE BREITE: links die Auswahl in Reihen
// (Kanal neben Art, die Gruppen im Spaltensatz, Personen
// nebeneinander), rechts ab xl eine Spalte mit dem Ergebnis, die so
// hoch ist wie die Auswahl und darin rollt. Darunter kommt man so
// schnell zum Text, wie die Auswahl es zulaesst.
// ============================================================
import { useMemo, useState } from "react";
import Link from "next/link";
import {
  ChevronDown,
  Mail,
  MessageSquareText,
  Newspaper,
  ShieldCheck,
  Stamp,
  Undo2,
  UserMinus,
} from "lucide-react";
import type { ContactStatus, MailingChannel } from "@/generated/prisma/enums";
import { Checkbox } from "@/components/ui/checkbox";
import { PLAETTCHEN, PLAETTCHEN_AN, PLAETTCHEN_AUS, Schiene, Zaehlknopf } from "@/components/liste";
import { KontaktAuswahl } from "@/components/mitglieder/kontakt-auswahl";
import { CONTACT_STATUS } from "@/lib/labels";
import { cn } from "@/lib/utils";
import {
  AUSSCHLUSSGRUND,
  BRIEFGRUND,
  MITGLIEDS_STATUS,
  kandidaten,
  mitUntergruppen,
  type Aufloesung,
  type Empfaenger,
} from "@/lib/nachrichten/empfaenger";
import type { AudienceView, Zweck } from "@/lib/nachrichten/views";
import type { ContactView, GroupView } from "@/lib/views";
import { Angabe, Hinweis } from "./zone";

const KANAELE: { wert: MailingChannel; label: string; icon: typeof Mail }[] = [
  { wert: "EMAIL", label: "E-Mail", icon: Mail },
  { wert: "SMS", label: "SMS", icon: MessageSquareText },
  { wert: "LETTER", label: "Brief", icon: Stamp },
];

const STATUS_WAHL: ContactStatus[] = [
  "ACTIVE",
  "PASSIVE",
  "HONORARY",
  "PROSPECT",
  "FORMER",
  "CONTACT",
];

export function KanalWahl({
  wert,
  onChange,
}: {
  wert: MailingChannel;
  onChange: (k: MailingChannel) => void;
}) {
  return (
    <Schiene className="w-fit">
      {KANAELE.map((k) => (
        <button
          key={k.wert}
          type="button"
          aria-pressed={wert === k.wert}
          onClick={() => onChange(k.wert)}
          className={cn(
            PLAETTCHEN,
            "text-body-sm gap-1.5 px-3",
            wert === k.wert ? cn(PLAETTCHEN_AN, "font-medium") : PLAETTCHEN_AUS,
          )}
        >
          <k.icon className="h-3.5 w-3.5" aria-hidden />
          {k.label}
        </button>
      ))}
    </Schiene>
  );
}

function ZweckWahl({ wert, onChange }: { wert: Zweck; onChange: (z: Zweck) => void }) {
  const optionen: { wert: Zweck; titel: string; text: string; icon: typeof Mail }[] = [
    {
      wert: "INFO",
      titel: "Vereinsinformation",
      text: "Einladung, Trainingszeiten, Beitrag — geht an alle gewählten Mitglieder.",
      icon: Mail,
    },
    {
      wert: "NEWSLETTER",
      titel: "Newsletter oder Werbung",
      text: "Rundbrief, Angebote, Sponsoren — nur an Personen mit Einwilligung, mit Abmeldelink.",
      icon: Newspaper,
    },
  ];
  return (
    <div role="radiogroup" className="grid gap-2 sm:grid-cols-2">
      {optionen.map((o) => {
        const an = wert === o.wert;
        return (
          <button
            key={o.wert}
            type="button"
            role="radio"
            aria-checked={an}
            onClick={() => onChange(o.wert)}
            className={cn(
              "flex items-start gap-3 rounded-lg border px-3.5 py-3 text-left transition-colors",
              an
                ? "border-accent/50 bg-accent-subtle"
                : "border-border hover:border-border-strong hover:bg-bg-secondary",
            )}
          >
            <span
              className={cn(
                "mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-full border",
                an ? "border-accent" : "border-border-control",
              )}
            >
              {an && <span className="bg-accent h-2 w-2 rounded-full" />}
            </span>
            <span className="min-w-0">
              <span className="text-body-sm text-text-primary flex items-center gap-1.5 font-medium">
                <o.icon className="text-text-tertiary h-3.5 w-3.5" aria-hidden />
                {o.titel}
              </span>
              <span className="text-caption text-text-secondary mt-0.5 block">{o.text}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

function GruppenWahl({
  gruppen,
  gewaehlt,
  onChange,
}: {
  gruppen: GroupView[];
  gewaehlt: string[];
  onChange: (ids: string[]) => void;
}) {
  const wurzeln = gruppen.filter((g) => !g.parentId);
  const kinder = (id: string) => gruppen.filter((g) => g.parentId === id);
  const ueber = mitUntergruppen(gewaehlt, gruppen);

  function zeile(g: GroupView, tiefe: number): React.ReactNode {
    const direkt = gewaehlt.includes(g.id);
    const geerbt = !direkt && ueber.has(g.id);
    const eltern = g.parentId ? gruppen.find((p) => p.id === g.parentId) : undefined;
    return (
      <div
        key={g.id}
        className={cn(
          tiefe === 0 ? "break-inside-avoid pb-1" : "border-border-subtle ml-2 border-l pl-4",
        )}
      >
        <Checkbox
          id={`gruppe-${g.id}`}
          checked={direkt || geerbt}
          disabled={geerbt}
          onChange={() =>
            onChange(direkt ? gewaehlt.filter((x) => x !== g.id) : [...gewaehlt, g.id])
          }
          label={g.name}
          hint={
            <span className="numeric">
              {g.memberCount} {g.memberCount === 1 ? "Person" : "Personen"}
              {geerbt && eltern
                ? ` · über ${eltern.name}`
                : g.emailAlias
                  ? ` · ${g.emailAlias.split("@")[0]}@…`
                  : ""}
            </span>
          }
          className="py-1"
        />
        {kinder(g.id).map((k) => zeile(k, tiefe + 1))}
      </div>
    );
  }

  // Spaltensatz statt Raster: eine Obergruppe mit ihren Untergruppen
  // bleibt beisammen, und kurze Gruppen fuellen die Luecke daneben.
  return (
    <div className="@container">
      <div className="columns-1 gap-x-8 @md:columns-2 @3xl:columns-3 @6xl:columns-4">
        {wurzeln.map((g) => zeile(g, 0))}
      </div>
    </div>
  );
}

function StatusWahl({
  gewaehlt,
  zaehler,
  onChange,
}: {
  gewaehlt: ContactStatus[];
  zaehler: Record<ContactStatus, number>;
  onChange: (s: ContactStatus[]) => void;
}) {
  return (
    <Schiene className="w-fit max-w-full [scrollbar-width:none] overflow-x-auto">
      {STATUS_WAHL.map((s) => {
        const an = gewaehlt.includes(s);
        return (
          <Zaehlknopf
            key={s}
            label={CONTACT_STATUS[s].label}
            anzahl={zaehler[s]}
            aktiv={an}
            onClick={() => onChange(an ? gewaehlt.filter((x) => x !== s) : [...gewaehlt, s])}
          />
        );
      })}
    </Schiene>
  );
}

// ── Der Zaehler ─────────────────────────────────────────────

function Personzeile({ e, aktion }: { e: Empfaenger; aktion?: React.ReactNode }) {
  const c = e.kontakt;
  const adresse =
    e.weg === "EMAIL"
      ? c.email
      : e.weg === "SMS"
        ? c.mobile
        : e.weg === "LETTER"
          ? `${c.street}, ${c.zip} ${c.city}`
          : e.grund
            ? AUSSCHLUSSGRUND[e.grund].label
            : "";
  return (
    <li className="group hover:bg-bg-primary flex items-center gap-3 rounded-md px-2 py-1.5">
      <div className="min-w-0 flex-1">
        <Link
          href={`/mitglieder/${c.id}`}
          target="_blank"
          className="text-body-sm text-text-primary hover:text-accent block truncate"
        >
          {c.displayName}
        </Link>
        <p className="text-caption text-text-tertiary truncate">
          {adresse}
          {e.briefgrund && <> · {BRIEFGRUND[e.briefgrund]}</>}
        </p>
      </div>
      {aktion}
    </li>
  );
}

function Abschnitt({
  titel,
  anzahl,
  children,
  ton,
}: {
  titel: string;
  anzahl: number;
  children: React.ReactNode;
  ton?: "danger";
}) {
  if (anzahl === 0) return null;
  return (
    <div>
      <p
        className={cn(
          "text-overline px-2 pb-1",
          ton === "danger" ? "text-danger" : "text-text-tertiary",
        )}
      >
        {titel} <span className="numeric">({anzahl})</span>
      </p>
      <ul>{children}</ul>
    </div>
  );
}

export function Zaehler({
  aufloesung,
  channel,
  onAusschliessen,
  onAufnehmen,
  spalte = false,
}: {
  aufloesung: Aufloesung;
  channel: MailingChannel;
  onAusschliessen?: (id: string) => void;
  onAufnehmen?: (id: string) => void;
  /** Als eigene Spalte (ab xl): Liste immer offen, so hoch wie der Platz, rollt darin. */
  spalte?: boolean;
}) {
  const [offen, setOffen] = useState(false);
  const z = aufloesung.zahlen;
  const teile: { text: string; ton?: "danger" | "warning" }[] = [];
  if (z.email) teile.push({ text: `${z.email} per Mail` });
  if (z.sms) teile.push({ text: `${z.sms} per SMS` });
  if (z.brief) teile.push({ text: `${z.brief} per Brief` });
  for (const [grund, n] of Object.entries(z.nachGrund) as [
    keyof typeof AUSSCHLUSSGRUND,
    number,
  ][]) {
    if (n)
      teile.push({
        text: `${n} ${AUSSCHLUSSGRUND[grund].kurz}${grund === "HAND" ? "" : " ausgeschlossen"}`,
        ton: grund === "HAND" ? undefined : "warning",
      });
  }

  const nachWeg = (w: "EMAIL" | "SMS" | "LETTER") => aufloesung.erreicht.filter((e) => e.weg === w);
  const knopf = (label: string, icon: React.ReactNode, tun: () => void) => (
    <button
      type="button"
      onClick={tun}
      className="text-caption text-text-tertiary hover:bg-bg-tertiary hover:text-text-primary flex h-7 shrink-0 items-center gap-1 rounded-md px-2 transition focus-visible:opacity-100 pointer-fine:opacity-0 pointer-fine:group-hover:opacity-100"
    >
      {icon}
      <span className="max-sm:sr-only">{label}</span>
    </button>
  );
  const leer = aufloesung.alle.length === 0;

  return (
    <div
      className={cn(
        "bg-bg-secondary ring-border-subtle flex flex-col rounded-xl ring-1 ring-inset",
        spalte && "xl:h-full",
      )}
      aria-live="polite"
    >
      <button
        type="button"
        onClick={() => setOffen((o) => !o)}
        aria-expanded={offen}
        disabled={leer}
        className={cn(
          "flex w-full items-start gap-3 px-4 py-3 text-left",
          spalte && "xl:pointer-events-none",
        )}
      >
        <span className="min-w-0 flex-1">
          {spalte && (
            <span className="text-overline text-text-tertiary mb-1 hidden xl:block">Ergebnis</span>
          )}
          <span className="text-heading-md text-text-primary numeric">{z.gesamt}</span>{" "}
          <span className="text-body-sm text-text-secondary">Empfänger</span>
          <span className="text-caption numeric mt-1 flex flex-wrap gap-x-2 gap-y-0.5">
            {teile.length === 0 && <span className="text-text-tertiary">Noch niemand gewählt</span>}
            {teile.map((t, i) => (
              <span
                key={t.text}
                className={cn(t.ton === "warning" ? "text-warning" : "text-text-secondary")}
              >
                {i > 0 && <span className="text-text-tertiary mr-2">·</span>}
                {t.text}
              </span>
            ))}
          </span>
        </span>
        {!leer && (
          <span
            className={cn(
              "text-caption text-text-secondary mt-1.5 flex shrink-0 items-center gap-1",
              spalte && "xl:hidden",
            )}
          >
            {offen ? "Liste verbergen" : "Liste zeigen"}
            <ChevronDown
              className={cn("h-3.5 w-3.5 transition-transform", offen && "rotate-180")}
              aria-hidden
            />
          </span>
        )}
      </button>

      {leer ? (
        spalte && (
          <p className="text-caption text-text-tertiary border-border-subtle hidden border-t px-4 py-3 xl:block">
            Hier steht, wer die Nachricht bekommt und auf welchem Weg — sobald links eine Gruppe,
            ein Status oder eine Person gewählt ist.
          </p>
        )
      ) : (
        <div
          className={cn(
            "border-border-subtle max-h-96 space-y-3 overflow-y-auto overscroll-contain border-t px-2 py-3",
            !offen && "hidden",
            spalte && "xl:block xl:max-h-none xl:min-h-0 xl:flex-1",
          )}
        >
          {(["EMAIL", "SMS", "LETTER"] as const).map((w) => {
            const liste = nachWeg(w);
            const titel =
              w === "EMAIL"
                ? "Per E-Mail"
                : w === "SMS"
                  ? "Per SMS"
                  : channel === "EMAIL"
                    ? "Bekommen einen Brief"
                    : "Per Brief";
            return (
              <Abschnitt key={w} titel={titel} anzahl={liste.length}>
                {liste.map((e) => (
                  <Personzeile
                    key={e.kontakt.id}
                    e={e}
                    aktion={
                      onAusschliessen &&
                      knopf("Ausschließen", <UserMinus className="h-3.5 w-3.5" aria-hidden />, () =>
                        onAusschliessen(e.kontakt.id),
                      )
                    }
                  />
                ))}
              </Abschnitt>
            );
          })}
          <Abschnitt
            titel="Nicht angeschrieben"
            anzahl={aufloesung.ausgeschlossen.length}
            ton="danger"
          >
            {aufloesung.ausgeschlossen.map((e) => (
              <Personzeile
                key={e.kontakt.id}
                e={e}
                aktion={
                  e.grund === "HAND" && onAufnehmen
                    ? knopf("Wieder aufnehmen", <Undo2 className="h-3.5 w-3.5" aria-hidden />, () =>
                        onAufnehmen(e.kontakt.id),
                      )
                    : undefined
                }
              />
            ))}
          </Abschnitt>
        </div>
      )}
    </div>
  );
}

// ── Die Zone ────────────────────────────────────────────────

export function EmpfaengerZone({
  channel,
  audience,
  aufloesung,
  kontakte,
  gruppen,
  fehler,
  onChannel,
  onAudience,
}: {
  channel: MailingChannel;
  audience: AudienceView;
  aufloesung: Aufloesung;
  kontakte: ContactView[];
  gruppen: GroupView[];
  fehler?: string;
  onChannel: (k: MailingChannel) => void;
  onAudience: (a: AudienceView) => void;
}) {
  const setze = (teil: Partial<AudienceView>) => onAudience({ ...audience, ...teil });

  // Wie viele Personen je Status in Frage kommen — in den gewaehlten
  // Gruppen, sonst im ganzen Verein.
  const statusZaehler = useMemo(() => {
    const ids = mitUntergruppen(audience.groups, gruppen);
    const basis = ids.size
      ? kontakte.filter((c) => c.groups.some((g) => ids.has(g.groupId)))
      : kontakte;
    return Object.fromEntries(
      STATUS_WAHL.map((s) => [s, basis.filter((c) => c.status === s).length]),
    ) as Record<ContactStatus, number>;
  }, [audience.groups, gruppen, kontakte]);

  const kandidatenListe = useMemo(
    () => kandidaten({ ...audience, exclude: [] }, kontakte, gruppen).map((k) => k.kontakt),
    [audience, kontakte, gruppen],
  );
  const keineMitglieder = aufloesung.erreicht.filter(
    (e) => !MITGLIEDS_STATUS.includes(e.kontakt.status),
  ).length;
  const ohneEinwilligung = aufloesung.zahlen.nachGrund.EINWILLIGUNG;

  const schnell: { label: string; tun: () => void }[] = [
    {
      label: "Alle Mitglieder",
      tun: () => setze({ groups: [], statuses: MITGLIEDS_STATUS, include: [], exclude: [] }),
    },
    {
      label: "Vorstand",
      tun: () =>
        setze({
          groups: gruppen.filter((g) => g.kind === "BOARD" && !g.parentId).map((g) => g.id),
          statuses: [],
          include: [],
          exclude: [],
        }),
    },
    {
      label: "Mit offenem Beitrag",
      tun: () =>
        setze({
          groups: [],
          statuses: [],
          include: kontakte.filter((c) => c.openAmount > 0).map((c) => c.id),
          exclude: [],
        }),
    },
  ];

  return (
    <div className="grid gap-x-8 gap-y-6 xl:grid-cols-[minmax(0,1fr)_minmax(300px,360px)]">
      <div className="min-w-0 space-y-6">
        <div className="grid gap-5 lg:grid-cols-[17rem_minmax(0,1fr)]">
          <Angabe
            titel="Kanal"
            hinweis={
              channel === "EMAIL"
                ? "Wer Post wünscht oder keine Mailadresse hat, bekommt dieselbe Nachricht als Brief."
                : channel === "SMS"
                  ? "Nur an Personen mit Handynummer. Kurz halten — die SMS hat keinen Betreff."
                  : "Gedruckt und versendet über den Briefdienst, mit Anschriftfeld für den Fensterumschlag."
            }
          >
            <KanalWahl wert={channel} onChange={onChannel} />
          </Angabe>

          <Angabe titel="Art der Nachricht">
            <ZweckWahl wert={audience.purpose} onChange={(purpose) => setze({ purpose })} />
            {audience.purpose === "NEWSLETTER" ? (
              <Hinweis
                ton="info"
                icon={<ShieldCheck className="text-accent-2 h-4 w-4" aria-hidden />}
              >
                Newsletter und Werbung gehen nur an Personen, die eingewilligt haben.
                {ohneEinwilligung > 0 && (
                  <>
                    {" "}
                    <strong className="numeric font-medium">{ohneEinwilligung}</strong>{" "}
                    {ohneEinwilligung === 1 ? "Person" : "Personen"} ohne Einwilligung{" "}
                    {ohneEinwilligung === 1 ? "wird" : "werden"} nicht angeschrieben.
                  </>
                )}{" "}
                Im Fuß steht der Abmeldelink.
              </Hinweis>
            ) : (
              keineMitglieder > 0 && (
                <Hinweis
                  ton="warning"
                  icon={<ShieldCheck className="text-warning h-4 w-4" aria-hidden />}
                >
                  Darunter <strong className="numeric font-medium">{keineMitglieder}</strong>{" "}
                  {keineMitglieder === 1 ? "Kontakt" : "Kontakte"} ohne Mitgliedschaft. Eine
                  Vereinsinformation darf sie nur erreichen, wenn sie sie betrifft — sonst
                  „Newsletter oder Werbung“ wählen.
                </Hinweis>
              )
            )}
          </Angabe>
        </div>

        <Angabe
          titel="Gruppen"
          hinweis="Eine Obergruppe schließt ihre Untergruppen ein."
          rechts={
            <div className="flex flex-wrap items-center justify-end gap-1">
              <span className="text-caption text-text-tertiary pr-1 max-sm:hidden">
                Schnellauswahl
              </span>
              {schnell.map((s) => (
                <button
                  key={s.label}
                  type="button"
                  onClick={s.tun}
                  className="text-caption text-text-secondary hover:bg-bg-tertiary hover:text-text-primary ring-border-subtle rounded-md px-2 py-1 ring-1 transition-colors ring-inset"
                >
                  {s.label}
                </button>
              ))}
            </div>
          }
        >
          <GruppenWahl
            gruppen={gruppen}
            gewaehlt={audience.groups}
            onChange={(groups) => setze({ groups })}
          />
        </Angabe>

        <Angabe
          titel="Status"
          hinweis={
            audience.groups.length
              ? "Schränkt die gewählten Gruppen ein. Ohne Auswahl zählt jeder Status."
              : "Ohne Gruppe: alle Personen mit diesem Status."
          }
        >
          <StatusWahl
            gewaehlt={audience.statuses}
            zaehler={statusZaehler}
            onChange={(statuses) => setze({ statuses })}
          />
        </Angabe>

        <div className="grid gap-5 md:grid-cols-2">
          <Angabe titel="Zusätzlich an">
            <KontaktAuswahl
              mehrfach
              values={audience.include}
              onChangeMany={(include) => setze({ include })}
              kontakte={kontakte}
              placeholder="Personen dazunehmen …"
            />
          </Angabe>
          <Angabe titel="Nicht an">
            <KontaktAuswahl
              mehrfach
              values={audience.exclude}
              onChangeMany={(exclude) => setze({ exclude })}
              kontakte={kandidatenListe}
              placeholder="Personen ausschließen …"
              disabled={kandidatenListe.length === 0}
            />
          </Angabe>
        </div>
      </div>

      {/* Ab xl so hoch wie die Auswahl links — die Liste rollt darin. */}
      <div className="min-w-0 space-y-2 xl:relative">
        <div className="xl:absolute xl:inset-0 xl:flex xl:flex-col xl:gap-2">
          <div className="xl:min-h-0 xl:flex-1">
            <Zaehler
              spalte
              aufloesung={aufloesung}
              channel={channel}
              onAusschliessen={(id) => setze({ exclude: [...audience.exclude, id] })}
              onAufnehmen={(id) => setze({ exclude: audience.exclude.filter((x) => x !== id) })}
            />
          </div>
          {fehler && (
            <p className="text-caption text-danger mt-2 xl:mt-0" role="alert">
              {fehler}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
